/**
 * Auto-Add Rewards System - Performance Optimized
 * Handles automatic gift addition and progress tracking
 */

(function() {
  'use strict';

  // Shared tracking for added products across all systems
  let addedProducts = new Set();
  
  // Global caches with TTL
  const globalCache = {
    products: new Map(),
    cart: { data: null, timestamp: 0, ttl: 2000 }, // 2 second cache for cart
    variants: new Map()
  };

  // Performance tracking
  const perfCounters = {
    apiCalls: 0,
    cacheHits: 0,
    lastReset: Date.now()
  };

  // Request throttling
  const activeRequests = new Set();
  
  // Global debounce timer
  let globalDebounceTimer = null;
  
  // Centralized update scheduler to replace multiple setTimeout calls
  const scheduledUpdates = new Map();
  let updateScheduleId = 0;

  function scheduleUpdate(callback, delay = 100, key = null) {
    const scheduleKey = key || `update_${++updateScheduleId}`;
    
    // Cancel existing update with same key
    if (scheduledUpdates.has(scheduleKey)) {
      clearTimeout(scheduledUpdates.get(scheduleKey));
    }
    
    const timeoutId = setTimeout(() => {
      scheduledUpdates.delete(scheduleKey);
      try {
        callback();
      } catch (error) {
        console.error('Error in scheduled update:', error);
      }
    }, delay);
    
    scheduledUpdates.set(scheduleKey, timeoutId);
    return scheduleKey;
  }

  // Global debounced update function
  function scheduleGlobalUpdate(callback, delay = 300) {
    if (globalDebounceTimer) {
      clearTimeout(globalDebounceTimer);
    }
    
    globalDebounceTimer = setTimeout(() => {
      try {
        callback();
      } catch (error) {
        console.error('Error in global update:', error);
      }
      globalDebounceTimer = null;
    }, delay);
  }

  // Debug helper function
  function debugLog(...args) {
    if (window.AutoAddConfig && window.AutoAddConfig.debug) {
      console.log('[Rewards Debug]', ...args);
    }
  }

  // Performance monitoring
  function logPerformance() {
    if (window.AutoAddConfig && window.AutoAddConfig.debug) {
      const now = Date.now();
      const elapsed = now - perfCounters.lastReset;
      if (elapsed > 10000) { // Log every 10 seconds
        console.log(`[Perf] API calls: ${perfCounters.apiCalls}, Cache hits: ${perfCounters.cacheHits}, Hit ratio: ${(perfCounters.cacheHits / (perfCounters.apiCalls + perfCounters.cacheHits) * 100).toFixed(1)}%`);
        perfCounters.apiCalls = 0;
        perfCounters.cacheHits = 0;
        perfCounters.lastReset = now;
      }
    }
  }

  // Optimized cart fetching with caching
  async function getCachedCart(forceRefresh = false) {
    const now = Date.now();
    const cartCache = globalCache.cart;
    
    if (!forceRefresh && cartCache.data && (now - cartCache.timestamp) < cartCache.ttl) {
      perfCounters.cacheHits++;
      return cartCache.data;
    }
    
    try {
      perfCounters.apiCalls++;
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      cartCache.data = cart;
      cartCache.timestamp = now;
      logPerformance();
      
      return cart;
    } catch (error) {
      console.error('Error fetching cart:', error);
      return cartCache.data || { items: [], total_price: 0, item_count: 0 };
    }
  }

  // Optimized product data fetching with global caching  
  async function getCachedProductData(handle) {
    if (!handle || typeof handle !== 'string' || handle === '[object Object]') {
      console.error('Invalid product handle:', handle);
      return null;
    }
    
    if (globalCache.products.has(handle)) {
      perfCounters.cacheHits++;
      return globalCache.products.get(handle);
    }
    
    const requestKey = `product-${handle}`;
    if (activeRequests.has(requestKey)) {
      // Wait for existing request
      while (activeRequests.has(requestKey)) {
        await new Promise(resolve => setTimeout(resolve, 10));
      }
      return globalCache.products.get(handle) || null;
    }
    
    activeRequests.add(requestKey);
    
    try {
      perfCounters.apiCalls++;
      const response = await fetch(`/products/${handle}.json`);
      if (response.ok) {
        const productData = await response.json();
        const productInfo = {
          variantId: productData.product.variants[0].id,
          featuredImage: productData.product.featured_image,
          images: productData.product.images,
          title: productData.product.title
        };
        
        globalCache.products.set(handle, productInfo);
        logPerformance();
        return productInfo;
      }
    } catch (error) {
      console.error('Error fetching product data:', error);
    } finally {
      activeRequests.delete(requestKey);
    }
    
    return null;
  }

  // Global function for auto-removing gifts (accessible from cart page interval)
  async function autoRemoveGifts(cart, cartTotal) {
    if (!window.AutoAddConfig || !window.AutoAddConfig.enabled) return;
    
    try {
      async function getVariantId(handle) {
        const productData = await getCachedProductData(handle);
        return productData?.variantId || null;
      }
      
      for (const product of window.AutoAddConfig.products) {
        // If cart total is below this product's threshold, remove the gift
        if (cartTotal < product.priceThreshold) {
          const variantId = await getVariantId(product.handle);
          if (!variantId) continue;

          // Check if this gift is in the cart
          const existingItem = cart.items.find(item => item.variant_id === variantId);
          if (existingItem) {
            // Remove the gift from cart
            const removeResponse = await fetch('/cart/update.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                updates: {
                  [variantId]: 0
                }
              })
            });

            if (removeResponse.ok) {
              // Clear tracking so it can be re-added when threshold is met again
              addedProducts.delete(variantId);
              addedProducts.delete(parseInt(variantId));
              addedProducts.delete(variantId.toString());
              
              // For non-cart pages, dispatch refresh event
              if (!window.themeVariables || !window.themeVariables.settings || window.themeVariables.settings.pageType !== "cart") {
                scheduleUpdate(() => {
                  document.dispatchEvent(new CustomEvent('cart:refresh'));
                }, 200);
              }
              // For cart pages, no additional action needed - the page redirect will handle the refresh
            }
          }
        }
      }
    } catch (error) {
      // Silent error handling
    }
  }

  // Cart Page Gift Removal Setup
  function setupCartPageGiftRemoval() {
    // Override the line-item quantity component's cart change method on cart pages
    const originalChangeLineItemQuantity = window.customElements.get('line-item-quantity');
    if (originalChangeLineItemQuantity) {
      // Store reference to original prototype method
      const originalMethod = originalChangeLineItemQuantity.prototype._changeLineItemQuantity;
      
      // Override the method
      originalChangeLineItemQuantity.prototype._changeLineItemQuantity = async function(lineKey, targetQuantity) {
        // Get current cart state before the change
        try {
          const cartResponse = await fetch('/cart.js');
          const currentCart = await cartResponse.json();
          const currentTotal = currentCart.total_price / 100;
          
          // Find the item being changed
          const changedItem = currentCart.items.find(item => item.key === lineKey);
          if (!changedItem) {
            // If item not found, proceed with original method
            return originalMethod.call(this, lineKey, targetQuantity);
          }
          
          // Calculate what the new total will be after this change
          const quantityDifference = targetQuantity - changedItem.quantity;
          const priceChange = quantityDifference * changedItem.price;
          const projectedTotal = (currentCart.total_price + priceChange) / 100;
          
          debugLog(`Cart change: ${changedItem.product_title} qty ${changedItem.quantity}→${targetQuantity}, total $${currentTotal}→$${projectedTotal}`);
          
          // Check if we need to remove any gifts based on the projected total
          await autoRemoveGiftsBeforeCartChange(projectedTotal, lineKey);
          
        } catch (error) {
          console.error('Error in cart page gift removal:', error);
        }
        
        // Proceed with the original cart change
        return originalMethod.call(this, lineKey, targetQuantity);
      };
    }
    
    // Also intercept direct URL-based removals (remove links)
    document.addEventListener('click', async function(event) {
      const removeLink = event.target.closest('a[href*="/cart/change"]');
      if (!removeLink) return;
      
      try {
        const url = new URL(removeLink.href);
        const lineKey = url.searchParams.get('id');
        const targetQuantity = parseInt(url.searchParams.get('quantity'));
        
        if (lineKey && targetQuantity === 0) {
          // This is a removal - get current cart and calculate new total
          const cartResponse = await fetch('/cart.js');
          const currentCart = await cartResponse.json();
          const itemToRemove = currentCart.items.find(item => item.key === lineKey);
          
          if (itemToRemove) {
            const projectedTotal = (currentCart.total_price - (itemToRemove.quantity * itemToRemove.price)) / 100;
            debugLog(`Removing ${itemToRemove.product_title}, projected total: $${projectedTotal}`);
            
            // Remove gifts if necessary before the page redirect
            await autoRemoveGiftsBeforeCartChange(projectedTotal, lineKey);
          }
        }
      } catch (error) {
        console.error('Error handling remove link:', error);
      }
    });
  }

  // Modified gift removal function for cart page (without page reload)
  async function autoRemoveGiftsBeforeCartChange(projectedTotal, excludeLineKey) {
    if (!window.AutoAddConfig || !window.AutoAddConfig.enabled) return;
    
    try {
      // Cache for variant IDs
      const variantCache = new Map();

      async function getVariantId(handle) {
        if (!handle || typeof handle !== 'string' || handle === '[object Object]') {
          return null;
        }
        
        if (variantCache.has(handle)) {
          return variantCache.get(handle);
        }
        
        try {
          const response = await fetch(`/products/${handle}.json`);
          if (response.ok) {
            const productData = await response.json();
            const variantId = productData.product.variants[0].id;
            variantCache.set(handle, variantId);
            return variantId;
          }
        } catch (error) {
          // Silent error handling
        }
        return null;
      }
      
      const cartResponse = await fetch('/cart.js');
      const currentCart = await cartResponse.json();
      
      for (const product of window.AutoAddConfig.products) {
        // If projected total is below this product's threshold, remove the gift
        if (projectedTotal < product.priceThreshold) {
          const variantId = await getVariantId(product.handle);
          if (!variantId) continue;

          // Find the gift in current cart
          const giftItem = currentCart.items.find(item => item.variant_id === variantId);
          if (giftItem && giftItem.key !== excludeLineKey) {
            debugLog(`Removing gift ${product.handle} (${product.label}) - projected total $${projectedTotal} below threshold $${product.priceThreshold}`);
            
            // Remove the gift immediately
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                updates: {
                  [variantId]: 0
                }
              })
            });
            
            // Clear tracking
            addedProducts.delete(variantId);
            addedProducts.delete(parseInt(variantId));
            addedProducts.delete(variantId.toString());
          }
        }
      }
    } catch (error) {
      console.error('Error in autoRemoveGiftsBeforeCartChange:', error);
    }
  }

  // Auto-Add System
  function initAutoAddSystem() {
    if (!window.AutoAddConfig || !window.AutoAddConfig.enabled) return;

    // Cache for variant IDs to avoid repeated API calls
    const variantCache = new Map();

    async function getVariantId(handle) {
      // Ensure handle is a valid string
      if (!handle || typeof handle !== 'string' || handle === '[object Object]') {
        console.error('Invalid product handle:', handle);
        return null;
      }
      
      if (variantCache.has(handle)) {
        return variantCache.get(handle);
      }
      
      try {
        const response = await fetch(`/products/${handle}.json`);
        if (response.ok) {
          const productData = await response.json();
          const variantId = productData.product.variants[0].id;
          variantCache.set(handle, variantId);
          return variantId;
        }
      } catch (error) {
        // Silent error handling
      }
      return null;
    }

    // Add a lock to prevent concurrent executions
    let autoAddInProgress = false;

    async function checkAndAutoAdd() {
      if (autoAddInProgress) {
        // Prevent concurrent executions
        return;
      }
      
      autoAddInProgress = true;
      
      try {
        let cart = await getCachedCart();
        const cartTotal = cart.total_price / 100;

        debugLog('Auto-add check: cart total $' + cartTotal, 'items:', cart.item_count);

        // Update cart count first
        updateCartCount(cart.item_count);

        if (cartTotal === 0) return;

        // Sort products by threshold to ensure we add gifts in the correct order
        const sortedProducts = [...window.AutoAddConfig.products].sort((a, b) => a.priceThreshold - b.priceThreshold);
        let giftsAdded = false;

        for (const product of sortedProducts) {
          if (cartTotal >= product.priceThreshold) {
            // Get variant ID from cache
            const productData = await getCachedProductData(product.handle);
            if (!productData?.variantId) continue;
            
            const variantId = productData.variantId;
            
            // Always check actual cart state, don't rely on addedProducts tracking alone
            const existingItems = cart.items.filter(item => item.variant_id === variantId);
            const currentQuantityInCart = existingItems.reduce((sum, item) => sum + item.quantity, 0);
            
            debugLog(`Gift ${product.handle}: threshold $${product.priceThreshold}, max qty: ${product.maxQuantity}, current in cart: ${currentQuantityInCart}`);
            
            // Only add if we haven't reached the max quantity for this gift
            if (currentQuantityInCart < product.maxQuantity) {
              const quantityToAdd = Math.min(1, product.maxQuantity - currentQuantityInCart);
              
              debugLog(`Adding ${quantityToAdd} x ${product.handle} (${product.label})`);
              
              // Add product to cart
              const addResponse = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
                },
                body: JSON.stringify({
                  id: variantId,
                  quantity: quantityToAdd
                })
              });
              
              if (addResponse.ok) {
                // Update our tracking
                addedProducts.add(variantId);
                addedProducts.add(parseInt(variantId));
                addedProducts.add(variantId.toString());
                
                // Refresh cart data for next iteration
                cart = await getCachedCart(true); // Force refresh
                giftsAdded = true;
                
                debugLog(`Successfully added ${product.handle}, cart now has ${cart.item_count} items`);
              } else {
                console.error(`Failed to add ${product.handle}`);
              }
            } else {
              // Mark as added in our tracking since it's already at max quantity
              addedProducts.add(variantId);
              addedProducts.add(parseInt(variantId));
              addedProducts.add(variantId.toString());
            }
          }
        }
        
        // Only dispatch refresh if we actually added gifts
        if (giftsAdded) {
          updateCartCount(cart.item_count);
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
        
      } catch (error) {
        console.error('Error in checkAndAutoAdd:', error);
      } finally {
        autoAddInProgress = false;
      }
    }


    // Function for handling manual cart changes with provided cart data
    async function handleCartChangeWithData(cart) {
      try {
        const cartTotal = cart.total_price / 100;
        
        // Update cart count first
        updateCartCount(cart.item_count);
        
        // Auto-remove gifts that no longer qualify on manual cart changes
        await autoRemoveGifts(cart, cartTotal);
        
        // Then check for new gifts to add after a delay
        scheduleUpdate(checkAndAutoAdd, 800, 'cart-change-add-gifts');
      } catch (error) {
        // Silent error handling
      }
    }
    
    // Separate function for handling manual cart changes (with gift removal)
    async function handleCartChange() {
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        const cartTotal = cart.total_price / 100;
        
        // Auto-remove gifts that no longer qualify on manual cart changes
        await autoRemoveGifts(cart, cartTotal);
        
        // Then check for new gifts to add
        scheduleUpdate(checkAndAutoAdd, 500, 'manual-cart-change');
      } catch (error) {
        // Silent error handling
      }
    }

    // Event listeners for auto-add - optimized with centralized scheduling
    scheduleUpdate(checkAndAutoAdd, 1000, 'initial-load');
    document.addEventListener('variant:add', () => scheduleUpdate(checkAndAutoAdd, 1200, 'variant-add'));
    document.addEventListener('cart:refresh', () => scheduleUpdate(checkAndAutoAdd, 800, 'cart-refresh'));
    document.addEventListener('cart:open', () => scheduleUpdate(checkAndAutoAdd, 600, 'cart-open'));
    
    // Handle cart page loads (since cart page uses full page reload for item changes)
    if (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === "cart") {
      // On cart page, check for gift removal immediately and after a short delay
      scheduleUpdate(handleCartChange, 500, 'cart-page-initial');
      scheduleUpdate(handleCartChange, 1200, 'cart-page-delayed');
    }
    
    // Listen for cart changes (including manual item removal)
    document.addEventListener('cart:change', (event) => {
      if (event.detail.baseEvent === 'line-item:change') {
        // Manual item removal/quantity change
        scheduleUpdate(() => handleCartChangeWithData(event.detail.cart), 300, 'line-item-change');
      } else {
        // Other cart changes
        scheduleUpdate(checkAndAutoAdd, 800, 'cart-change-other');
      }
    });
    
    // Listen for quick-buy button clicks
    document.addEventListener('click', function(e) {
      if (e.target.closest('.product-card__quick-buy') || 
          e.target.closest('[data-action="add-to-cart"]') ||
          e.target.classList.contains('quick-buy-button')) {
        scheduleUpdate(checkAndAutoAdd, 1200, 'quick-buy-click');
      }
    });
    
    // Listen for form submissions (quick-buy forms)
    document.addEventListener('submit', function(e) {
      if (e.target.closest('.product-card') || 
          e.target.classList.contains('product-form') ||
          e.target.querySelector('[data-action="add-to-cart"]')) {
        scheduleUpdate(checkAndAutoAdd, 1200, 'quick-buy-submit');
      }
    });

    // Function to sync tracking with actual cart state
    async function syncTrackingWithCart(cart) {
      // Clear tracking and rebuild based on actual cart contents
      addedProducts.clear();
      
      // Add all gift variants currently in cart to tracking
      for (const product of window.AutoAddConfig.products) {
        const productData = await getCachedProductData(product.handle);
        if (productData?.variantId) {
          const existingItem = cart.items.find(item => item.variant_id === productData.variantId);
          if (existingItem) {
            addedProducts.add(productData.variantId);
            addedProducts.add(parseInt(productData.variantId));
            addedProducts.add(productData.variantId.toString());
          }
        }
      }
    }

    // Reset when cart is cleared
    document.addEventListener('cart:clear', () => {
      addedProducts.clear();
      // Clear cart cache when cart is cleared
      globalCache.cart.data = null;
      globalCache.cart.timestamp = 0;
    });
    
    // Sync tracking on page load
    scheduleUpdate(async () => {
      try {
        const cart = await getCachedCart();
        await syncTrackingWithCart(cart);
      } catch (error) {
        // Silent error handling
      }
    }, 500, 'initial-sync');
  }

  // Progress Bar System - Optimized
  function initProgressBarSystem() {
    // Proper debouncing and content caching to prevent flashing
    let updateInProgress = false;
    let lastRenderedContent = {
      currentAmount: '',
      nextTarget: '',
      progressWidth: '',
      targetsList: '',
      giftsList: ''
    };
    
    // Use global debounced update instead of local timer
    function debouncedUpdate() {
      scheduleGlobalUpdate(updateCartRewardsProgress, 200);
    }
    
    async function updateCartRewardsProgress() {
      if (updateInProgress) return;
      updateInProgress = true;
      
      try {
        const rewardsSection = document.querySelector('.auto-add-rewards');
        if (!rewardsSection) return;
        
        const currentAmountEl = document.querySelector('.current-amount');
        const nextTargetEl = document.querySelector('.next-target');
        const progressBar = document.querySelector('.progress-bar');
        const rewardTargetsList = document.querySelector('.reward-targets-list');
        const addedGiftsSection = document.querySelector('.added-gifts');
        const giftItemsContainer = document.querySelector('.gift-items');
        
        if (!currentAmountEl || !nextTargetEl || !progressBar) return;
      
      try {
        const cart = await getCachedCart();
        const cartTotal = (cart.total_price || 0) / 100;
        const sortedProducts = [...window.AutoAddConfig.products].sort((a, b) => a.priceThreshold - b.priceThreshold);
        
        // Build list of all targets and their status with product images
        const targetsListItems = [];
        for (const product of sortedProducts) {
          // Build progress display for this product
          const amountNeeded = product.priceThreshold - cartTotal;
          const productData = await getCachedProductData(product.handle);
          
          // Fix image path handling
          let productImage = null;
          if (productData?.featuredImage && typeof productData.featuredImage === 'string') {
            productImage = productData.featuredImage;
          } else if (productData?.images && Array.isArray(productData.images) && productData.images.length > 0) {
            productImage = typeof productData.images[0] === 'string' ? productData.images[0] : productData.images[0]?.src;
          }
          
          const imageHtml = productImage ? 
            `<img src="${productImage}" style="width: 48px; height: 48px; border-radius: 6px; object-fit: cover; margin-right: 12px;" alt="${product.productName || product.label}">` :
            `<div style="width: 48px; height: 48px; border-radius: 6px; background: #ddd; display: flex; align-items: center; justify-content: center; margin-right: 12px;"><span style="font-size: 24px;">🎁</span></div>`;
          
          if (amountNeeded > 0) {
            targetsListItems.push({
              html: `<div class="reward-target-item h-stack gap-0 align-center" style="padding: 4px 0;">${imageHtml}<span class="text-sm" style="color: #666;">$${Math.ceil(amountNeeded)} more to unlock <strong>${product.productName || product.label}</strong></span></div>`,
              isUnlocked: false
            });
          } else {
            targetsListItems.push({
              html: `<div class="reward-target-item h-stack gap-0 align-center" style="padding: 4px 0;">${imageHtml}<span class="text-sm" style="color: #28a745;">✅ <strong>${product.productName || product.label}</strong> unlocked</span></div>`,
              isUnlocked: true
            });
          }
        }
        
        // Find next threshold for progress bar
        let nextThreshold = null;
        for (const product of sortedProducts) {
          if (cartTotal < product.priceThreshold) {
            nextThreshold = product.priceThreshold;
            break;
          }
        }
        
        // Prepare new content
        const newCurrentAmount = '$' + Math.floor(cartTotal);
        let newNextTarget, newProgressWidth;
        
        if (nextThreshold) {
          newNextTarget = '$' + nextThreshold;
          const progressPercent = Math.min(100, (cartTotal / nextThreshold) * 100);
          newProgressWidth = progressPercent + '%';
        } else {
          // All rewards unlocked - show current cart total
          newNextTarget = '$' + Math.floor(cartTotal);
          newProgressWidth = '100%';
        }
        
        const newTargetsList = targetsListItems.map(item => item.html).join('');
        
        // Only update DOM if content has changed
        if (lastRenderedContent.currentAmount !== newCurrentAmount) {
          currentAmountEl.textContent = newCurrentAmount;
          lastRenderedContent.currentAmount = newCurrentAmount;
        }
        
        if (lastRenderedContent.nextTarget !== newNextTarget) {
          nextTargetEl.textContent = newNextTarget;
          lastRenderedContent.nextTarget = newNextTarget;
        }
        
        if (lastRenderedContent.progressWidth !== newProgressWidth) {
          progressBar.style.width = newProgressWidth;
          lastRenderedContent.progressWidth = newProgressWidth;
        }
        
        if (rewardTargetsList && lastRenderedContent.targetsList !== newTargetsList) {
          rewardTargetsList.innerHTML = newTargetsList;
          lastRenderedContent.targetsList = newTargetsList;
        }
        
        // Update gifts display - using cached product data
        const giftsInCart = [];
        for (const product of sortedProducts) {
          const productData = await getCachedProductData(product.handle);
          if (productData && productData.variantId) {
            const cartItem = cart.items.find(item => item.variant_id === productData.variantId);
            if (cartItem) {
              giftsInCart.push({ 
                ...product, 
                cartItem, 
                productTitle: cartItem.product_title, 
                variantId: productData.variantId,
                featuredImage: productData.featuredImage
              });
            }
          }
        }
          
          // Prepare gifts HTML content
          const newGiftsList = giftsInCart.map(gift => {
            // Fix image path handling for added gifts
            let productImage = null;
            if (gift.featuredImage && typeof gift.featuredImage === 'string') {
              productImage = gift.featuredImage;
            } else if (gift.cartItem?.image && typeof gift.cartItem.image === 'string') {
              productImage = gift.cartItem.image;
            } else if (gift.cartItem?.featured_image && typeof gift.cartItem.featured_image === 'string') {
              productImage = gift.cartItem.featured_image;
            }
            
            const imageHtml = productImage ? 
              `<img src="${productImage}" style="width: 64px; height: 64px; border-radius: 8px; object-fit: cover;" alt="${gift.productTitle || gift.label}">` :
              `<div style="width: 64px; height: 64px; border-radius: 8px; background: #ddd; display: flex; align-items: center; justify-content: center;"><span style="font-size: 32px;">🎁</span></div>`;
            
            return `
              <div class="gift-item h-stack gap-3" style="padding: 8px; background: #f0f8f0; border: 1px solid #28a745; border-radius: 6px;">
                ${imageHtml}
                <div class="v-stack gap-0 grow">
                  <p class="text-sm" style="font-weight: 500; margin: 0; color: #333;">${gift.productTitle || gift.label}</p>
                  <p class="text-xs" style="margin: 0; color: #666;">${gift.label}</p>
                </div>
                <div class="h-stack gap-2">
                  <span class="text-xs" style="color: #28a745; font-weight: 500;">FREE</span>
                </div>
              </div>
            `;
          }).join('');
          
          // Only update gifts display if content changed
          if (addedGiftsSection && giftItemsContainer) {
            if (giftsInCart.length > 0) {
              if (lastRenderedContent.giftsList !== newGiftsList) {
                addedGiftsSection.style.display = 'block';
                giftItemsContainer.innerHTML = newGiftsList;
                lastRenderedContent.giftsList = newGiftsList;
              }
            } else {
              if (lastRenderedContent.giftsList !== '') {
                addedGiftsSection.style.display = 'none';
                lastRenderedContent.giftsList = '';
              }
            }
          }
        } catch (error) {
          console.error('Error updating rewards progress:', error);
        }
      } finally {
        updateInProgress = false;
      }
    }

    // Event listeners for progress bar - all using debounced updates
    document.addEventListener('cart:open', debouncedUpdate);
    document.addEventListener('cart:refresh', debouncedUpdate);
    document.addEventListener('variant:add', debouncedUpdate);
    
    // Listen for quick-buy button clicks to trigger progress updates
    document.addEventListener('click', function(e) {
      if (e.target.closest('.product-card__quick-buy') || 
          e.target.closest('[data-action="add-to-cart"]') ||
          e.target.classList.contains('quick-buy-button')) {
        scheduleUpdate(debouncedUpdate, 1200, 'progress-click');
      }
    });
    
    // Listen for form submissions (quick-buy forms)
    document.addEventListener('submit', function(e) {
      if (e.target.closest('.product-card') || 
          e.target.classList.contains('product-form') ||
          e.target.querySelector('[data-action="add-to-cart"]')) {
        scheduleUpdate(debouncedUpdate, 1200, 'progress-submit');
      }
    });
    
    // More targeted DOM observer - only watch for rewards section changes
    const rewardsContainer = document.querySelector('.auto-add-rewards') || document.body;
    const observer = new MutationObserver(function(mutations) {
      let shouldUpdate = false;
      mutations.forEach(function(mutation) {
        if (mutation.addedNodes.length > 0) {
          Array.from(mutation.addedNodes).forEach(node => {
            if (node.nodeType === 1 && (
              node.classList?.contains('auto-add-rewards') || 
              node.querySelector?.('.auto-add-rewards') ||
              node.classList?.contains('quick-buy-drawer__info') ||
              node.querySelector?.('.quick-buy-drawer__info')
            )) {
              shouldUpdate = true;
            }
          });
        }
      });
      
      if (shouldUpdate) {
        debouncedUpdate();
      }
    });
    
    // Observe a more specific container instead of entire document.body
    observer.observe(rewardsContainer, { childList: true, subtree: true });
    
    // Listen for drawer content updates specifically
    const initDrawerObserver = () => {
      const cartNotificationDrawer = document.querySelector('cart-notification-drawer');
      if (cartNotificationDrawer) {
        const drawerObserver = new MutationObserver(debouncedUpdate);
        drawerObserver.observe(cartNotificationDrawer, { childList: true, subtree: true });
        return true;
      }
      return false;
    };
    
    // Try immediate initialization
    if (!initDrawerObserver()) {
      document.addEventListener('DOMContentLoaded', initDrawerObserver);
    }
    
    // Quick initial update
    scheduleUpdate(debouncedUpdate, 200, 'progress-initial');
  }


  // Utility function to update cart count
  function updateCartCount(itemCount) {
    document.querySelectorAll('cart-count').forEach(element => {
      element.textContent = itemCount;
      element.innerHTML = itemCount;
      if (itemCount > 0) {
        element.classList.remove('opacity-0');
        element.style.opacity = '1';
      }
      if (typeof element.update === 'function') {
        element.update();
      }
      element.setAttribute('count', itemCount);
    });
  }

  // Initialize all systems
  document.addEventListener('DOMContentLoaded', function() {
    if (window.AutoAddConfig && window.AutoAddConfig.enabled) {
      debugLog('AutoAddConfig loaded:', window.AutoAddConfig.products);
      initAutoAddSystem();
      initProgressBarSystem();
      
      // Better handling for cart page - intercept cart changes before page reload
      if (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === "cart") {
        setupCartPageGiftRemoval();
      }
    }
  });

})();